import React, { useEffect, useState } from 'react';
import AttachmentCard from './components/AttachmentCard';
import { fetchPlanningSheet } from 'src/shared/services/src/lib/homeLearning';
import { ChildrenStoreData, PlanningResponseType, PlanningSheetType, ResourceDocumentType } from 'src/shared/types/src';
import { useAppSelector } from '../../services/store';
import { getCurrentChild } from 'src/shared/redux-state/src';
import LocalImages from '../../static';
import { AxiosError } from 'axios';
import { APP_MESSAGES, DATE_FORMATS } from 'src/shared/config/src';
import { dateFormatter } from 'src/shared/utils/src';
import { endOfISOWeek, startOfISOWeek } from 'date-fns';
import HomeLearningBaseScreen from './HomeLearningBaseScreen';
import { BaseCard, CustomButton, PlanningLoader } from 'src/app/components';
import NextPrevWeekDates from 'src/app/components/commonComponents/NextPrevWeekDates';
import { URL_CONSTANTS } from 'src/app/config/appConstants';
import { toast } from "react-toastify";
import { getParamsFromQuery, getQueryString, navigateToDownloadAppScreen, encodeStringForQuery } from 'src/app/utilities/helpers';

const Planning = () => {


    const currentChild: ChildrenStoreData = useAppSelector(getCurrentChild);

    const [fromDate, setFromDate] = useState<string>('');
    const [toDate, setToDate] = useState<string>('');
    const [planningData, setPlanningData] = useState<PlanningSheetType | null>(null)
    const [loading, setLoading] = useState<boolean>(false);
    const [isFirstTime, setIsFirstTime] = useState<boolean>(true);

    useEffect(() => {
        const searchParams = getParamsFromQuery()
        const fromDate: any = searchParams?.get(URL_CONSTANTS.FROM) ? searchParams.get(URL_CONSTANTS.FROM) : dateFormatter(startOfISOWeek(new Date()), DATE_FORMATS.YMD)
        const toDate: any = searchParams?.get(URL_CONSTANTS.TO) ? searchParams.get(URL_CONSTANTS.TO) : dateFormatter(endOfISOWeek(new Date()), DATE_FORMATS.YMD)

        const query: string = getQueryString({ from: fromDate, to: toDate })

        const q = encodeStringForQuery(query);
        if (!q) window.history.replaceState({}, '', window.location.pathname)
        else window.history.replaceState({}, '', `${window.location.pathname}?q=${q}`);

        setFromDate(fromDate);
        setToDate(toDate);
    }, []);

    useEffect(() => {
        if(!isFirstTime) init();
        setIsFirstTime(false);
    }, [fromDate])

    const init = async () => {
        try {
            setLoading(true);

            const response = await fetchPlanningSheet({
                child: currentChild,
                date: fromDate
            }) as PlanningResponseType;

            if (response && response.message === 'success' && response.planningSheet && Object.keys(response.planningSheet).length > 0) {
                setPlanningData(response?.planningSheet)
            } else {
                setPlanningData(null);
            }

        } catch (error) {
            console.log("Error fetching planning", error);
            if (error instanceof AxiosError) {
                // const apiError = error as AxiosError;
                const { errors } = error?.response?.data;
                if (errors && Object.keys(errors).length > 0) {
                    toast.error(errors.length > 0 ? errors.join(',') : errors.join(", "), { autoClose: 5000 });
                } else {
                    toast.error(APP_MESSAGES.generalErrorMessage, { autoClose: 5000 });
                };
            } else {
                toast.error(APP_MESSAGES.generalErrorMessage, { autoClose: 5000 });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleStartEndChange = async ({ from, to }: { from: string, to: string }) => {
        if (from && to) {
            const query: string = getQueryString({ from, to })

            const q = encodeStringForQuery(query);
            if (!q) window.history.replaceState({}, '', window.location.pathname)
            else window.history.replaceState({}, '', `${window.location.pathname}?q=${q}`);
        
            setFromDate(from);
            setToDate(to);
        }
    }

    const renderAttachments = (item: ResourceDocumentType) => {
        return (
            <AttachmentCard
                title={item?.name}
                date={item?.created}
                description={item?.description}
                mimeType={item?.mimeType}
                type={item?.type}
                url={item?.url}
            />
        )
    };

    const renderEmptyData = () => {

        return (
            <div className='flex-1 d-flex flex-column justify-content-center align-items-center'>
                <div className='planning-no-data'>
                    <img src={LocalImages.noPlanning} alt="no planning" />
                    <p className='text-center fs-12 text-gray-300 mx-auto mt-3'>The nursery has not shared any planning with you yet. You will receive a notification when it has been shared.</p>
                </div>
            </div>
        )
    }

    const renderPlanningData = () => {
        return (
            <>
                <div className='mg-t-24'>
                    <div className='fs-14 text-black fw-semibold'>Common Learning Intentions/Activities</div>
                    <p className='mb-0 fs-12 text-gray-300 mg-t-5'>Common learning intentions are a more general outline of planning for all children at a similar age.</p>
                    <BaseCard customBaseCardStyle='mg-t-10 rounded-3 pd-15'>
                        <div className='fs-12 text-black'>
                            {planningData?.learningIntentions || ''}
                        </div>
                    </BaseCard>
                </div>
                
                <div className='mg-t-20'>
                    <div className='fs-14 text-black fw-semibold'>Planned Area and Activity</div>
                    <p className='mb-0 fs-12 text-gray-300 mg-t-5'>Planned learning areas and activities specifically for your child.</p>
                    <BaseCard customBaseCardStyle='mg-t-10 rounded-3 pd-15'>
                        <div className='fs-12 text-black'>
                            {planningData?.plannedAreaActivity || ''}
                        </div>
                    </BaseCard>
                    
                    <CustomButton 
                        customBtnStyle='mg-t-20'
                        onClick={navigateToDownloadAppScreen}
                        text='Observe Activity'
                        color='primary'
                    />
                </div>

                {planningData && planningData?.documents.length > 0 && (
                    <>
                        <div className='mg-t-20'>
                            <div className='fs-14 text-black fw-semibold'>Linked Resources</div>
                            <p  className='mb-0 fs-12 text-gray-300 mg-t-5'>These are the documents related to this week’s planning sheet.</p>
                        </div>

                        {planningData?.documents.map((el: any) => {
                            return renderAttachments(el)
                        })}
                    </>
                )}

            </>
        )
    }

    return (
        <HomeLearningBaseScreen>

            <div className='mg-t-25'>
                <NextPrevWeekDates setStartEndOfSelectedWeek={handleStartEndChange} canLoad={!loading} from={fromDate} to={toDate} />
            </div>

            <>
                {loading && (
                    <PlanningLoader />
                )}
                {!loading && !planningData && renderEmptyData()}
                {!loading && planningData && renderPlanningData()}

            </>
        </HomeLearningBaseScreen>
    )
};

export default Planning;